import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { FaSearchPlus, FaTimes } from "react-icons/fa";
import { resolveLabelsConvidado } from "../../services/dossieConvidadoLabels";
import headerPaginaConvidado from "../../assets/dossie/header_pagina_convidado.png";
import footerPaginaConvidado from "../../assets/dossie/footer_pagina_convidado.png";
import molduraFotoConvidado from "../../assets/dossie/moldura_foto_convidado.png";
import "./admDossieConvidado.css";

function CampoInstituicao({ rotulo, valor }) {
  return (
    <p className="adc-campo">
      <span className="adc-campo-rotulo">{rotulo} </span>
      <span className="adc-campo-valor">{valor}</span>
    </p>
  );
}

/**
 * Ficha individual do dossiê (16:9), espelhando o layout do PDF gerado em
 * dossieConvidadosLayout.ts.
 * @param {{ convidado: object, embedded?: boolean, numeroPagina?: number }} props
 */
export default function DossieConvidadoSheet({
  convidado,
  embedded = false,
  numeroPagina = 1,
}) {
  const [zoomAberto, setZoomAberto] = useState(false);
  const labels = useMemo(
    () => resolveLabelsConvidado(convidado),
    [convidado],
  );

  useEffect(() => {
    if (!zoomAberto) return undefined;

    const onKey = (event) => {
      if (event.key === "Escape") setZoomAberto(false);
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [zoomAberto]);

  if (!convidado) return null;

  const temFoto = Boolean(convidado.avatar);

  return (
    <>
      <article
        className={`adc-sheet${embedded ? " adc-sheet--embedded" : ""}`}
        aria-label={`Página do dossiê de ${labels.nomeExibicao}`}
      >
        <div className="adc-header-wrap">
          <img
            className="adc-chrome adc-chrome--header"
            src={headerPaginaConvidado}
            alt=""
            aria-hidden
          />
          <h1 className="adc-titulo-instituicao">{labels.empresaExibicao}</h1>
        </div>

        <div className="adc-body">
          <div className="adc-col adc-col--perfil">
            {!embedded ? (
              <>
                <h2 className="adc-nome">{labels.nomeExibicao}</h2>
                <p className="adc-cargo">{labels.cargo}</p>
              </>
            ) : null}

            <div className="adc-foto-wrap">
              <div className="adc-foto-furo">
                {temFoto ? (
                  <button
                    type="button"
                    className="adc-foto-btn"
                    onClick={() => setZoomAberto(true)}
                    title="Ampliar foto"
                    aria-label={`Ampliar foto de ${labels.nomeExibicao}`}
                  >
                    <img
                      className="adc-foto"
                      src={convidado.avatar}
                      alt=""
                    />
                  </button>
                ) : (
                  <div className="adc-foto-placeholder">Sem foto</div>
                )}
              </div>
              <img
                className="adc-moldura"
                src={molduraFotoConvidado}
                alt=""
                aria-hidden
              />
              {temFoto ? (
                <span className="adc-foto-hint" aria-hidden>
                  <FaSearchPlus size={14} />
                </span>
              ) : null}
            </div>

            <div className="adc-hobby-row">
              <p className="adc-hobby-label">Atividades que tenho interesse:</p>
              <span className="adc-pill" title={labels.hobby}>
                {labels.hobby}
              </span>
            </div>
          </div>

          <div className="adc-col adc-col--bio">
            <h3 className="adc-section-title">Mini Bio</h3>
            <p className="adc-bio">{labels.bio}</p>
          </div>

          <div className="adc-col adc-col--instituicao">
            <h3 className="adc-section-title adc-section-title--instituicao">
              Dados da Instituição
            </h3>
            <CampoInstituicao rotulo="Segmento:" valor={labels.segmento} />
            {labels.complementoRotulo && (
              <CampoInstituicao
                rotulo={labels.complementoRotulo}
                valor={labels.complementoValor}
              />
            )}
            <CampoInstituicao
              rotulo="Faturamento estimado 2025:"
              valor={labels.faturamento}
            />
            <p className="adc-campo adc-campo--dupla">
              <span className="adc-campo-item">
                <span className="adc-campo-rotulo">Estado: </span>
                <span className="adc-campo-valor">{labels.estado}</span>
              </span>
              <span className="adc-campo-item">
                <span className="adc-campo-rotulo">Região: </span>
                <span className="adc-campo-valor">{labels.regiao}</span>
              </span>
            </p>
            <CampoInstituicao
              rotulo="Prioridade de Investimento 26/27:"
              valor={labels.prioridade}
            />
            <CampoInstituicao
              rotulo="Qual o maior desafio da sua instituição no momento?"
              valor={labels.transformacao}
            />
            <p className="adc-campo">
              <span className="adc-campo-rotulo">O que você procura SBF?</span>
            </p>
            <span className="adc-fita" title={labels.procuraSbf}>
              {labels.procuraSbf}
            </span>
          </div>
        </div>

        <img
          className="adc-chrome adc-chrome--footer"
          src={footerPaginaConvidado}
          alt=""
          aria-hidden
        />

        <div className="adc-numero-pagina">{numeroPagina}</div>
      </article>

      {zoomAberto && temFoto
        ? createPortal(
            <div
              className="adc-zoom-overlay"
              role="dialog"
              aria-modal="true"
              aria-label={`Foto ampliada de ${labels.nomeExibicao}`}
              onClick={() => setZoomAberto(false)}
            >
              <button
                type="button"
                className="adc-zoom-close"
                onClick={() => setZoomAberto(false)}
                aria-label="Fechar zoom"
              >
                <FaTimes size={16} />
              </button>
              <figure
                className="adc-zoom-figure"
                onClick={(event) => event.stopPropagation()}
              >
                <img src={convidado.avatar} alt={labels.nomeExibicao} />
                <figcaption>
                  <strong>{labels.nomeExibicao}</strong>
                  {labels.cargo ? <span>{labels.cargo}</span> : null}
                </figcaption>
              </figure>
            </div>,
            document.body
          )
        : null}
    </>
  );
}
